/**
 * Task #1 — Admin error log viewer with copy-for-agent button.
 * Rendered inside the Developer Settings section of the settings dialog (admin only).
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle, Check, ChevronDown, ChevronRight, Copy, RefreshCw, Trash2,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

interface ErrorLogRow {
  id: number;
  errorHash: string;
  source: string;
  message: string;
  stack: string | null;
  componentStack: string | null;
  route: string | null;
  method: string | null;
  statusCode: number | null;
  userAgent: string | null;
  environment: string;
  context: Record<string, any>;
  recentUsers: Array<{ id: number; name: string; role: string }>;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

const SOURCE_LABELS: Record<string, string> = {
  server: 'Server',
  client: 'Client',
  'client-boundary': 'UI Crash',
  'client-api': 'API Call',
};

function buildCopyText(e: ErrorLogRow): string {
  const lines: string[] = [];
  lines.push('The following error was logged on the published app: ');
  lines.push('');
  lines.push(`[Source] ${SOURCE_LABELS[e.source] || e.source}`);
  if (e.route) lines.push(`[Route] ${e.method ? e.method + ' ' : ''}${e.route}${e.statusCode ? ` (status ${e.statusCode})` : ''}`);
  lines.push(`[Error Message] ${e.message}`);
  if (e.stack) lines.push(`[Stack]\n${e.stack}`);
  if (e.componentStack) lines.push(`[Component Stack]\n${e.componentStack}`);
  if (e.context && Object.keys(e.context).length > 0) {
    lines.push(`[Context]\n${JSON.stringify(e.context, null, 2)}`);
  }
  if (e.recentUsers?.length) {
    lines.push(`[Affected Users] ${e.recentUsers.map(u => `${u.name} (${u.role})`).join(', ')}`);
  }
  if (e.userAgent) lines.push(`[Browser] ${e.userAgent}`);
  lines.push(`[Environment] ${e.environment}`);
  lines.push(`[First Seen] ${e.firstSeenAt}`);
  lines.push(`[Last Seen] ${e.lastSeenAt}`);
  lines.push(`[Occurrences] ${e.occurrenceCount}`);
  lines.push('');
  lines.push('Note: stack traces from the published app may reference minified code; use sourcemaps or line hints from the route/component stack to locate the source.');
  return lines.join('\n');
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function ErrorLogSettings() {
  const queryClient = useQueryClient();
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const { data, isLoading, refetch, isFetching } = useQuery<{ errors: ErrorLogRow[] }>({
    queryKey: ['/api/admin/error-logs', sourceFilter !== 'all' ? { source: sourceFilter } : null],
    staleTime: 30_000,
  });

  const clearMutation = useMutation({
    mutationFn: () => apiRequest('DELETE', '/api/admin/error-logs'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/error-logs'] }),
  });

  const errors = data?.errors || [];

  const handleCopy = async (e: ErrorLogRow) => {
    try {
      await navigator.clipboard.writeText(buildCopyText(e));
      setCopiedId(e.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // clipboard unavailable (e.g. insecure context) — ignore
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium text-white">Error Log</span>
          <span className="text-xs text-gray-400">({errors.length} shown)</span>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="h-8 w-[130px] text-xs bg-gray-800 border-gray-600 text-gray-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="server">Server</SelectItem>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="client-boundary">UI Crash</SelectItem>
              <SelectItem value="client-api">API Call</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm" variant="outline"
            className="h-8 border-gray-600 text-gray-300 hover:bg-gray-700"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            size="sm" variant="outline"
            className="h-8 border-gray-600 text-red-400 hover:bg-red-900/30"
            onClick={() => {
              if (window.confirm('Clear all logged errors? This cannot be undone.')) {
                clearMutation.mutate();
              }
            }}
            disabled={clearMutation.isPending || errors.length === 0}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400 py-4 text-center">Loading…</div>
      ) : errors.length === 0 ? (
        <div className="text-sm text-gray-400 py-4 text-center">No errors logged. 🎉</div>
      ) : (
        <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
          {errors.map((e) => (
            <div key={e.id} className="border border-gray-700 rounded-md bg-gray-800/50">
              <button
                type="button"
                className="w-full flex items-center gap-2 px-2 py-2 text-left hover:bg-gray-700/50 rounded-md"
                onClick={() => setExpanded(expanded === e.id ? null : e.id)}
              >
                {expanded === e.id
                  ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                <Badge variant="outline" className="text-[10px] border-gray-600 text-gray-300 shrink-0">
                  {SOURCE_LABELS[e.source] || e.source}
                </Badge>
                <span className="text-xs text-gray-200 truncate flex-1">{e.message}</span>
                {e.occurrenceCount > 1 && (
                  <Badge className="text-[10px] bg-amber-900/60 text-amber-300 shrink-0">×{e.occurrenceCount}</Badge>
                )}
                <span className="text-[10px] text-gray-500 shrink-0">{formatTime(e.lastSeenAt)}</span>
              </button>
              {expanded === e.id && (
                <div className="px-3 pb-3 space-y-2 text-xs text-gray-300">
                  {e.route && (
                    <div><span className="text-gray-500">Route:</span> {e.method ? `${e.method} ` : ''}{e.route}{e.statusCode ? ` → ${e.statusCode}` : ''}</div>
                  )}
                  {e.recentUsers?.length > 0 && (
                    <div><span className="text-gray-500">Users:</span> {e.recentUsers.map(u => `${u.name} (${u.role})`).join(', ')}</div>
                  )}
                  <div><span className="text-gray-500">First seen:</span> {formatTime(e.firstSeenAt)} · <span className="text-gray-500">Last:</span> {formatTime(e.lastSeenAt)}</div>
                  {e.stack && (
                    <pre className="bg-gray-900 rounded p-2 overflow-auto max-h-40 text-[10px] text-gray-400 whitespace-pre-wrap">{e.stack}</pre>
                  )}
                  {e.componentStack && (
                    <pre className="bg-gray-900 rounded p-2 overflow-auto max-h-32 text-[10px] text-gray-400 whitespace-pre-wrap">{e.componentStack}</pre>
                  )}
                  {e.context && Object.keys(e.context).length > 0 && (
                    <pre className="bg-gray-900 rounded p-2 overflow-auto max-h-32 text-[10px] text-gray-400 whitespace-pre-wrap">{JSON.stringify(e.context, null, 2)}</pre>
                  )}
                  <Button
                    size="sm" variant="outline"
                    className="h-7 border-gray-600 text-blue-300 hover:bg-blue-900/30 text-xs"
                    onClick={() => handleCopy(e)}
                  >
                    {copiedId === e.id
                      ? <><Check className="w-3 h-3 mr-1" /> Copied</>
                      : <><Copy className="w-3 h-3 mr-1" /> Copy for Agent</>}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
